// Using window object to ensure global accessibility

// Define a unique key for our map instance in the window object
const MAP_INSTANCE_KEY = '__MAP_INSTANCE__';

// Initialize the global state if it doesn't exist
if (!window[MAP_INSTANCE_KEY]) {
    window[MAP_INSTANCE_KEY] = {
        instance: null,
        initialized: false,
        headlessInitialized: false
    };
}

/**
 * Saves the map state to the server
 */
export const saveMapToServer = async (mapState) => {
    try {
        const response = await fetch('/api/maps', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(mapState),
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`Failed to save map: ${response.statusText}`);
        }

        // Get the GUID from the response
        const data = await response.json();
        const mapGuid = data.id;

        const protocol = window.location.protocol;
        const host = window.location.host;
        const mapUrl = `${protocol}//${host}/map/${mapGuid}`;

        //console.log('Map saved successfully with ID:', mapGuid);
        return mapUrl;
    } catch (error) {
        //console.error('Error saving map:', error);
        throw error;
    }
};

/**
 * Saves a map using the provided map data
 */
export const saveMap = async (mapInstance, orderId, customerId, orderlineId, panels, campaignType, customerName, campaignName) => {
    try {
        // Create the map state object
        const mapState = {
            orderId: orderId,
            customerId: customerId,
            mapData: {
                orderlineIds: orderlineId,
                customerName: customerName,
                campaignName: campaignName,
                center: mapInstance ? {
                    lat: mapInstance.getCenter().lat(),
                    lng: mapInstance.getCenter().lng()
                } : (panels.length > 0 && panels[0].latitude ?
                    { lat: panels[0].latitude, lng: panels[0].longitude } :
                    { lat: 0, lng: 0 }),
                zoom: mapInstance ? mapInstance.getZoom() : 10,
                panels: panels,
                campaignType: campaignType,
                savedAt: new Date().toISOString()
            }
        };

        return await saveMapToServer(mapState);
    } catch (error) {
        //console.error('Error in saveMap:', error);
        throw error;
    }
};

/**
 * Registers a map instance
 */
export const registerMapInstance = (mapData) => {
    const mapInstance = {
        saveMap: mapData.saveMapCallback ||
            // If no custom callback provided, create a function that uses our standard saveMap
            (async () => {
                if (mapData.mapInstance) {
                    return await saveMap(
                        mapData.mapInstance,
                        mapData.orderId,
                        mapData.customerId,
                        mapData.orderlineIds,
                        mapData.panels || [],
                        mapData.campaignType,
                        mapData.customerName,
                        mapData.campaignName
                    );
                } else {
                    throw new Error('No map instance provided and no custom save function');
                }
            }),
        mapInstance: mapData.mapInstance,
        isFromVisualMap: mapData.isFromVisualMap !== undefined ? mapData.isFromVisualMap : true,
        orderId: mapData.orderId,
        customerId: mapData.customerId,
        orderlineId: mapData.orderlineId,
        panels: mapData.panels,
        campaignType: mapData.campaignType,
        customerName: mapData.customerName,
        campaignName: mapData.campaignName
    };

    // Store in global window object
    window[MAP_INSTANCE_KEY].instance = mapInstance;
    window[MAP_INSTANCE_KEY].initialized = true;

    return mapInstance;
};

/**
 * Gets the current map instance
 */
export const getMapInstance = () => {
    return window[MAP_INSTANCE_KEY].instance;
};

/**
 * Checks if any map instance has been registered
 * @param {string} [orderId] - Optional order ID to check against
 * @param {string} [customerId] - Optional customer ID to check against
 * @returns {boolean} True if a map instance exists (and matches the IDs if provided), false otherwise
 */
export const hasMapInstance = (orderId, customerId, orderlineId) => {
    const instance = window[MAP_INSTANCE_KEY].instance;
    const initialized = window[MAP_INSTANCE_KEY].initialized;

    // If no instance exists or it's not initialized, return false
    if (!initialized || !instance) {
        return false;
    }

    // If orderId and customerId are provided, check if the instance matches
    if (orderId !== undefined && customerId !== undefined && orderlineId !== undefined) {
        return instance.orderId === orderId && instance.customerId === customerId && instance.orderlineId === orderlineId;
    }

    // Otherwise, just check if an instance exists
    return true;
};

/**
 * Logs debug information about the current map instance
 */
export const debugMapInstance = () => {
    const state = window[MAP_INSTANCE_KEY];
    console.log('Global map instance status:', {
        exists: !!state.instance,
        initialized: state.initialized,
        headlessInitialized: state.headlessInitialized,
        hasSaveMap: state.instance && typeof state.instance.saveMap === 'function',
        isFromVisualMap: state.instance && state.instance.isFromVisualMap || false
    });
};

/**
 * Clears the current map instance
 */
export const clearMapInstance = () => {
    window[MAP_INSTANCE_KEY].instance = null;
    window[MAP_INSTANCE_KEY].initialized = false;
    window[MAP_INSTANCE_KEY].headlessInitialized = false;
};

/**
 * Initializes a headless map service that can save maps without rendering
 * @returns {Promise<void>} A promise that resolves when initialization is complete
 */
export const initializeHeadlessMap = async (orderId, customerId, orderlineId, campaignType, customerName, campaignName, panels = [], forceReinitialization = false) => {
    // Check if we already have a map instance for this specific order and customer
    const hasMatchingInstance = hasMapInstance(orderId, customerId, orderlineId);

    // If already initialized with matching IDs and no force flag, skip initialization
    if (window[MAP_INSTANCE_KEY].headlessInitialized && hasMatchingInstance && !forceReinitialization) {
        //console.log('Headless map initialization skipped - already initialized for this order/customer');
        return;
    }

    // If force reinitialization or we have a different order/customer, clear the existing instance
    if (forceReinitialization || (window[MAP_INSTANCE_KEY].initialized && !hasMatchingInstance)) {
        clearMapInstance();
    }

    try {
        // Register a headless map instance
        registerMapInstance({
            saveMapCallback: async () => {
                return await saveMap(null, orderId, customerId, orderlineId, panels, campaignType, customerName, campaignName);
            },
            isFromVisualMap: false,
            isHeadless: true,
            orderId,
            customerId,
            orderlineId,
            panels,
            campaignType,
            customerName,
            campaignName
        });

        window[MAP_INSTANCE_KEY].headlessInitialized = true;
    } catch (error) {
        console.error('Error initializing headless map:', error);
        throw error;
    }
};